import { useCallback, useEffect, useState } from "react";
import { ChevronRight, Plus } from "lucide-react";
import { api, type Vendor } from "../api";
import { Select } from "./Select";
import { bestaetigen, fehlerMelden, fehlerText, toast } from "./verwaltung";

const SOURCE_LABEL: Record<string, string> = {
  email_attachment: "PDF im Mail-Anhang",
  email_link: "Link in der Mail",
  portal: "Portal-Login",
  api: "Anbieter-API",
  manuell: "von Hand",
};

const TAX_LABEL: Record<string, string> = {
  regulaer: "Deutsche Umsatzsteuer",
  reverse_charge: "Reverse-Charge §13b",
  ig_erwerb: "Innergemeinschaftlicher Erwerb",
  steuerfrei: "Ohne Umsatzsteuer",
  kein_beleg: "Kein steuerlicher Beleg",
};

const empty = (): Partial<Vendor> => ({
  name: "",
  matchDomains: [],
  defaultTax: "regulaer",
  defaultVatRate: 19,
  cadence: "monatlich",
  expectedDay: null,
  sourceType: "email_attachment",
  sourceConfig: {},
  active: true,
});

/** Startvorlagen für die häufigsten Fälle in einer deutschen Agentur. */
const PRESETS: { label: string; vendor: Partial<Vendor> }[] = [
  {
    label: "Adobe",
    vendor: {
      name: "Adobe",
      legalName: "Adobe Systems Software Ireland Ltd",
      matchDomains: ["adobe.com", "mail.adobe.com"],
      defaultTax: "reverse_charge",
      defaultVatRate: 0,
      cadence: "monatlich",
      expectedDay: 5,
      sourceType: "portal",
      active: true,
    },
  },
  {
    label: "Telekom",
    vendor: {
      name: "Telekom",
      legalName: "Telekom Deutschland GmbH",
      matchDomains: ["telekom.de", "t-online.de"],
      defaultTax: "regulaer",
      defaultVatRate: 19,
      cadence: "monatlich",
      expectedDay: 8,
      sourceType: "portal",
      active: true,
    },
  },
  {
    label: "Hetzner",
    vendor: {
      name: "Hetzner",
      legalName: "Hetzner Online GmbH",
      matchDomains: ["hetzner.com", "hetzner.de"],
      defaultTax: "regulaer",
      defaultVatRate: 19,
      cadence: "monatlich",
      expectedDay: 3,
      sourceType: "email_attachment",
      active: true,
    },
  },
  {
    label: "Google Workspace",
    vendor: {
      name: "Google",
      legalName: "Google Ireland Limited",
      matchDomains: ["google.com", "payments.google.com"],
      defaultTax: "reverse_charge",
      defaultVatRate: 0,
      cadence: "monatlich",
      expectedDay: 5,
      sourceType: "email_link",
      active: true,
    },
  },
];

export function VendorsPage() {
  const [vendors, setVendors] = useState<Vendor[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | "neu" | null>(null);
  const [draft, setDraft] = useState<Partial<Vendor> | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setVendors(await api.vendors());
      setLadeFehler(null);
    } catch (e) {
      setLadeFehler(fehlerText(e));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const schliessen = () => { setDraft(null); setOpenId(null); };

  const save = async () => {
    if (!draft?.name?.trim()) return;
    setBusy(true);
    try {
      if (draft.id) await api.updateVendor(draft.id, draft);
      else await api.createVendor(draft);
      toast.erfolg(draft.id ? `Quelle „${draft.name}" gespeichert.` : `Quelle „${draft.name}" angelegt.`);
      schliessen();
      await load();
    } catch (e) {
      fehlerMelden(e, "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const loeschen = async (v: Vendor) => {
    const ja = await bestaetigen({
      titel: `Quelle „${v.name}" löschen?`,
      text: "Bereits abgelegte Belege bleiben erhalten. Der Agent meldet danach aber nicht mehr, wenn von hier etwas ausbleibt.",
      bestaetigen: "Löschen",
      gefahr: true,
    });
    if (!ja) return;
    try {
      await api.deleteVendor(v.id);
      toast.erfolg(`Quelle „${v.name}" gelöscht.`);
      schliessen();
      await load();
    } catch (e) {
      fehlerMelden(e, "Löschen fehlgeschlagen");
    }
  };

  const neu = (vorlage: Partial<Vendor>) => { setDraft({ ...vorlage }); setOpenId("neu"); };

  return (
    <div className="page">
      <div className="v-kopf">
        <div>
          <h2>Belegquellen</h2>
          <p className="lede">
            Wer regelmäßig Rechnungen schickt, gehört hier hinein. Dann weiß der Agent, woher der Beleg kommt,
            wie er steuerlich einzuordnen ist — und meldet, wenn einer ausbleibt.
          </p>
        </div>
        <div className="v-kopf-aktionen">
          <button className="btn primary" onClick={() => neu(empty())} disabled={openId === "neu"}>
            <Plus aria-hidden="true" /> Quelle hinzufügen
          </button>
        </div>
      </div>

      {openId === "neu" && draft && (
        <div className="v-panel">
          <div className="v-panel-rumpf">
            <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>Neue Quelle</h3>
            <VendorForm draft={draft} onChange={setDraft} onSave={save} onCancel={schliessen} busy={busy} />
          </div>
        </div>
      )}

      {ladeFehler && !vendors ? (
        <div className="v-leer" data-art="fehler">
          <strong>Die Quellen ließen sich nicht laden</strong>
          <span>{ladeFehler}</span>
          <button className="btn" onClick={() => void load()}>Erneut versuchen</button>
        </div>
      ) : !vendors ? (
        <div className="v-laden" role="status">Lade Quellen …</div>
      ) : vendors.length === 0 && openId !== "neu" ? (
        <div className="v-leer">
          <strong>Noch keine Belegquellen</strong>
          <span>Häufige Anbieter mit passender steuerlicher Voreinstellung als Startpunkt:</span>
          <div className="v-reihe" style={{ justifyContent: "center", marginTop: 8 }}>
            {PRESETS.map((p) => (
              <button key={p.label} className="btn" onClick={() => neu(p.vendor)}>{p.label}</button>
            ))}
          </div>
        </div>
      ) : (
        vendors.map((v) => {
          const offen = openId === v.id;
          return (
            <div className="rule" key={v.id} data-disabled={!v.active}>
              <div
                className="rule-head"
                role="button"
                tabIndex={0}
                aria-expanded={offen}
                onClick={() => (offen ? schliessen() : (setOpenId(v.id), setDraft({ ...v })))}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); (e.currentTarget as HTMLElement).click(); } }}
              >
                <ChevronRight className="v-aufklapp" data-offen={offen} aria-hidden="true" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="rule-name">{v.name}</div>
                  <div className="rule-summary">
                    {SOURCE_LABEL[v.sourceType] ?? v.sourceType} · {TAX_LABEL[v.defaultTax] ?? v.defaultTax} · {CADENCE[v.cadence] ?? v.cadence}
                    {v.matchDomains.length > 0 ? ` · ${v.matchDomains.join(", ")}` : ""}
                  </div>
                </div>
                {v.sourceType === "portal" && (
                  <span className="chip" style={{ ["--chip-color" as string]: v.hasCredentials ? "var(--ok)" : "var(--warn)" }}>
                    {v.hasCredentials ? "Zugang hinterlegt" : "Zugang fehlt"}
                  </span>
                )}
                {!v.active && <span className="v-marke">pausiert</span>}
              </div>

              {offen && draft && (
                <div className="rule-body">
                  <VendorForm draft={draft} onChange={setDraft} onSave={save} onCancel={schliessen} busy={busy}
                    onDelete={() => void loeschen(v)} />
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

const CADENCE: Record<string, string> = {
  monatlich: "monatlich",
  viertel: "vierteljährlich",
  jaehrlich: "jährlich",
  unregelmaessig: "unregelmäßig",
};

interface FormProps {
  draft: Partial<Vendor>;
  onChange: (v: Partial<Vendor>) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  busy: boolean;
}

function VendorForm({ draft, onChange, onSave, onCancel, onDelete, busy }: FormProps) {
  const [credentials, setCredentials] = useState({ benutzer: "", passwort: "" });
  const [test, setTest] = useState<{ found: number; errors: string[]; screenshot: string | null } | null>(null);
  const [testet, setTestet] = useState(false);
  const [speichertZugang, setSpeichertZugang] = useState(false);
  // Freitext-Felder halten ihren eigenen Text: Kommas und halbes JSON dürfen
  // beim Tippen nicht sofort wieder verschwinden.
  const [domains, setDomains] = useState((draft.matchDomains ?? []).join(", "));
  const [rezept, setRezept] = useState(JSON.stringify(draft.sourceConfig ?? {}, null, 2));
  const [rezeptFehler, setRezeptFehler] = useState<string | null>(null);
  const set = (patch: Partial<Vendor>) => onChange({ ...draft, ...patch });

  const runTest = async () => {
    if (!draft.id) return;
    setTestet(true);
    try {
      setTest(await api.testVendor(draft.id));
    } catch (err) {
      setTest({ found: 0, errors: [fehlerText(err)], screenshot: null });
    } finally {
      setTestet(false);
    }
  };

  const zugangSpeichern = async () => {
    if (!draft.id) return;
    setSpeichertZugang(true);
    try {
      await api.setCredentials(draft.id, credentials);
      setCredentials({ benutzer: "", passwort: "" });
      toast.erfolg("Zugang verschlüsselt gespeichert.");
    } catch (e) {
      fehlerMelden(e, "Zugang nicht gespeichert");
    } finally {
      setSpeichertZugang(false);
    }
  };

  const kannSpeichern = Boolean(draft.name?.trim()) && !rezeptFehler && !busy;

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (kannSpeichern) onSave(); }} style={{ paddingTop: 6 }}>
      <div className="v-raster">
        <div className="field">
          <label htmlFor="q-name">Name<span className="v-pflicht" aria-hidden="true">*</span></label>
          <input id="q-name" className="input" required autoFocus={!draft.id} value={draft.name ?? ""}
            onChange={(e) => set({ name: e.target.value })} placeholder="Adobe" />
        </div>
        <div className="field">
          <label htmlFor="q-firma">Firmierung auf der Rechnung <span className="v-optional">(optional)</span></label>
          <input id="q-firma" className="input" value={draft.legalName ?? ""} onChange={(e) => set({ legalName: e.target.value })}
            placeholder="Adobe Systems Software Ireland Ltd" />
        </div>
        <div className="field v-breit">
          <label htmlFor="q-domains">Absender-Domains</label>
          <input
            id="q-domains"
            className="input mono"
            value={domains}
            onChange={(e) => {
              setDomains(e.target.value);
              set({ matchDomains: e.target.value.split(/[,\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean) });
            }}
            placeholder="adobe.com, mail.adobe.com"
          />
          <p className="hint">Mehrere durch Komma getrennt. Mails von dort ordnet der Agent dieser Quelle zu.</p>
        </div>
      </div>

      <div className="v-raster" data-spalten="3">
        <div className="field">
          <label>Steuerliche Behandlung</label>
          <Select
            value={draft.defaultTax ?? "regulaer"}
            onChange={(v) => set({ defaultTax: v })}
            options={Object.entries(TAX_LABEL).map(([k, label]) => ({ value: k, label }))}
            aria-label="Steuerliche Behandlung"
          />
        </div>
        <div className="field">
          <label htmlFor="q-satz">Steuersatz (%)</label>
          <input id="q-satz" className="input v-num" type="number" min={0} max={100} step="0.1" value={draft.defaultVatRate ?? ""}
            onChange={(e) => set({ defaultVatRate: e.target.value === "" ? null : Number(e.target.value) })} />
        </div>
        <div className="field">
          <label>Rhythmus</label>
          <Select
            value={draft.cadence ?? "monatlich"}
            onChange={(v) => set({ cadence: v })}
            options={Object.entries(CADENCE).map(([value, label]) => ({ value, label }))}
            aria-label="Rhythmus"
          />
        </div>
      </div>

      <div className="v-raster">
        <div className="field">
          <label htmlFor="q-tag">Erwartet um den … des Monats <span className="v-optional">(optional)</span></label>
          <input id="q-tag" className="input" type="number" min={1} max={31} value={draft.expectedDay ?? ""}
            onChange={(e) => set({ expectedDay: e.target.value === "" ? null : Math.min(31, Math.max(1, Number(e.target.value))) })} placeholder="5" />
        </div>
        <div className="field">
          <label>Woher kommt der Beleg?</label>
          <Select
            value={draft.sourceType ?? "email_attachment"}
            onChange={(v) => set({ sourceType: v })}
            options={Object.entries(SOURCE_LABEL).map(([k, label]) => ({ value: k, label }))}
            aria-label="Woher kommt der Beleg?"
          />
        </div>
      </div>

      {draft.sourceType === "portal" && (
        <div className="notice" data-art="warn" style={{ marginBottom: 16 }}>
          <strong>Abholung aus dem Kundenportal</strong>
          <p style={{ margin: "4px 0 12px" }}>
            Der unzuverlässigste Weg: Baut der Anbieter sein Portal um, bricht die Abholung. Der Testlauf zeigt
            mit einem Bildschirmfoto, wo es hakt. Zwei-Faktor-Anmeldungen lassen sich nicht automatisieren.
          </p>

          {!draft.id ? (
            <p className="hint" style={{ margin: "0 0 12px" }}>Zugang und Testlauf gibt es nach dem ersten Speichern.</p>
          ) : (
            <div className="v-raster" data-spalten="3" style={{ alignItems: "end" }}>
              <div className="field">
                <label htmlFor="q-benutzer">Benutzername</label>
                <input id="q-benutzer" className="input" autoComplete="off" value={credentials.benutzer}
                  onChange={(e) => setCredentials({ ...credentials, benutzer: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="q-passwort">Passwort</label>
                <input id="q-passwort" className="input" type="password" autoComplete="new-password" value={credentials.passwort}
                  onChange={(e) => setCredentials({ ...credentials, passwort: e.target.value })} />
              </div>
              <div className="field">
                <button type="button" className="btn" disabled={speichertZugang || !credentials.benutzer || !credentials.passwort}
                  onClick={() => void zugangSpeichern()}>
                  {speichertZugang ? "Speichert …" : draft.hasCredentials ? "Zugang ersetzen" : "Zugang speichern"}
                </button>
              </div>
            </div>
          )}

          <div className="field">
            <label htmlFor="q-rezept">Ablauf im Portal (JSON)</label>
            <textarea
              id="q-rezept"
              className="textarea mono"
              style={{ minHeight: 150 }}
              spellCheck={false}
              aria-invalid={rezeptFehler ? true : undefined}
              value={rezept}
              onChange={(e) => {
                setRezept(e.target.value);
                try {
                  const wert = e.target.value.trim() === "" ? {} : JSON.parse(e.target.value) as unknown;
                  if (!wert || typeof wert !== "object" || Array.isArray(wert)) throw new Error("Erwartet wird ein Objekt in { … }.");
                  setRezeptFehler(null);
                  set({ sourceConfig: wert as Record<string, unknown> });
                } catch (err) {
                  setRezeptFehler(err instanceof Error ? err.message : String(err));
                }
              }}
            />
            {rezeptFehler
              ? <p className="error" style={{ margin: 0 }}>Kein gültiges JSON: {rezeptFehler}</p>
              : <p className="hint">Schritte der Anmeldung und der Selektor für den Download-Link.</p>}
          </div>

          {draft.id && (
            <button type="button" className="btn" disabled={testet} onClick={() => void runTest()}>
              {testet ? "Testlauf läuft …" : "Testlauf (ohne Download)"}
            </button>
          )}

          {test && (
            <div style={{ marginTop: 12 }}>
              <p className={test.errors.length === 0 ? "ok-line" : "fail-line"} style={{ alignItems: "flex-start" }}>
                {test.errors.length === 0
                  ? `Hat geklappt: ${test.found} ${test.found === 1 ? "Download-Link" : "Download-Links"} gefunden.`
                  : test.errors.join(" · ")}
              </p>
              {test.screenshot && (
                <img
                  src={`/api/documents/screenshot?path=${encodeURIComponent(test.screenshot)}`}
                  alt="Bildschirmfoto an der Stelle, an der der Testlauf abbrach"
                  style={{ maxWidth: "100%", borderRadius: 10, border: "1px solid var(--line)", marginTop: 8 }}
                />
              )}
            </div>
          )}
        </div>
      )}

      <div className="v-checks">
        <label className="v-check">
          <input type="checkbox" checked={draft.active ?? true} onChange={(e) => set({ active: e.target.checked })} />
          Aktiv — fehlende Belege dieser Quelle melden
        </label>
      </div>

      <div className="v-knoepfe">
        <button type="submit" className="btn primary" disabled={!kannSpeichern}>{busy ? "Speichert …" : draft.id ? "Speichern" : "Anlegen"}</button>
        <button type="button" className="btn quiet" onClick={onCancel}>Abbrechen</button>
        <div className="spacer" />
        {onDelete && <button type="button" className="btn danger" onClick={onDelete}>Löschen</button>}
      </div>
    </form>
  );
}
